import { type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { selectedItemsSelector } from '@air/redux-selected-items';
import { type Board, type Clip, type Tag } from '@air/types';

import { DeleteCanvasConfirmationModal } from '~/components/Canvas/DeleteCanvasConfirmationModal';
import { useGetCanvasMenuOptionsCallback } from '~/components/Canvas/hooks/useGetCanvasMenuOptionsCallback';
import { MoveCanvasToBoardModal } from '~/components/Canvas/MoveCanvasToBoardModal/MoveCanvasToBoardModal';
import { filterCanvasTopBarMoreMenuOptions } from '~/components/Canvas/utils/filterCanvasTopBarMoreMenuOptions';
import { BatchExportImagesModal } from '~/components/ExportImagesModal/BatchExportImagesModal';
import { GalleryItemType } from '~/components/Gallery/types';
import { useAddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/useAddAssetsToBoardsModal';
import { AddToBoardButton } from '~/components/v2/components/shared/selectionBar/AddToBoardButton';
import { AddToCanvasButton } from '~/components/v2/components/shared/selectionBar/AddToCanvasButton';
import { CustomFieldsButton } from '~/components/v2/components/shared/selectionBar/CustomFieldsButton';
import { DownloadButton } from '~/components/v2/components/shared/selectionBar/DownloadButton';
import { EditTagsButton } from '~/components/v2/components/shared/selectionBar/EditTagsButton';
import { FavoriteButton } from '~/components/v2/components/shared/selectionBar/FavoriteButton';
import { InsertButton } from '~/components/v2/components/shared/selectionBar/InsertButton';
import { ShareButton } from '~/components/v2/components/shared/selectionBar/ShareButton';
import { ShareCollectionButton } from '~/components/v2/components/shared/selectionBar/ShareCollectionButton';
import { useGetPrivateAssetMenuOptionsCallback } from '~/components/v2/hooks/useGetPrivateAssetMenuOptionsCallback';
import { useGetPrivateBoardMenuOptionsCallback } from '~/components/v2/hooks/useGetPrivateBoardMenuOptionsCallback';
import { usePrivateSelectionMenuOptions } from '~/components/v2/hooks/usePrivateSelectionMenuOptions';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCanConsumeMoreCredits } from '~/hooks/useCanConsumeMoreCredits';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import {
  hasOnlyCanvasesSelectedSelector,
  hasOnlyClipsSelectedSelector,
  selectedBoardsSelector,
  selectedClipsHasUnfavorited,
  selectedFileClipsSelector,
  selectedMediaClipsSelector,
} from '~/store/selectedItems/selectors';
import { isSelectableGalleryItem, type SelectableGalleryCanvasItem } from '~/store/selectedItems/types';
import { useListCanvases } from '~/swr-hooks/canvases/useListCanvases';
import { isClipExportable } from '~/utils/Imgix';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import {
  canAddAssetToBoard,
  canChangeAssetCustomFields,
  canDownloadVersion,
  canEditClipTags,
  canRemoveClipTags,
} from '~/utils/permissions/assetPermissions';
import { canCreateShortUrl, canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';
import { useAirStore } from '~/utils/ReduxUtils';

export type UsePrivateSelectionBarMenuProps = {
  collectionsEnabled: boolean;
  canDownloadAllClips: boolean;
  canDownloadAllBoards: boolean;
  onBulkDownload: () => void;
  onBoardFavorite: (board: Board) => void;
  onBoardShare: (boardId: string) => void;
  onCanvasShare: (canvasId: string) => void;
  onBoardDownload: (boardId: string) => void;
  onClipFavorite: (clips: Clip[]) => void;
  onClipShare: (clip: Clip, boardContext?: string) => void;
  onAssetDownload: (id: string) => void;
  onSingleClipEditTags: (clipId: Clip['id'], tags: Tag[]) => void;
  onMultipleClipsEditTags: () => void;
  onCollectionShare: () => void;
  onAddToCanvas?: () => void;
  onInsert?: () => void;
  insertCount?: number;
};

export const usePrivateSelectionBarMenu = ({
  collectionsEnabled,
  canDownloadAllClips,
  canDownloadAllBoards,
  onBulkDownload,
  onBoardFavorite,
  onBoardShare,
  onCanvasShare,
  onBoardDownload,
  onClipFavorite,
  onClipShare,
  onAssetDownload,
  onSingleClipEditTags,
  onMultipleClipsEditTags,
  onCollectionShare,
  onAddToCanvas,
  onInsert,
  insertCount,
}: UsePrivateSelectionBarMenuProps) => {
  // Selectors
  const selectedItems = useSelector(selectedItemsSelector);
  const selectedBoardItems = useSelector(selectedBoardsSelector);
  const selectedMediaClipItems = useSelector(selectedMediaClipsSelector);
  const selectedFileItems = useSelector(selectedFileClipsSelector);
  const hasOnlyClipsSelected = useSelector(hasOnlyClipsSelectedSelector);
  const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);
  const store = useAirStore();
  const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());

  // Menu options callbacks
  const { getBoardMenuOptionsCallback } = useGetPrivateBoardMenuOptionsCallback();
  const { getAssetMenuOptionsCallback } = useGetPrivateAssetMenuOptionsCallback();
  const selectedItem0 = selectedItems[0];
  const singleSelectedCanvas =
    selectedItems.length === 1 &&
    selectedItem0 &&
    isSelectableGalleryItem(selectedItem0) &&
    selectedItem0.type === GalleryItemType.canvas
      ? selectedItem0.item
      : undefined;
  const { getCanvasMenuOptionsCallback } = useGetCanvasMenuOptionsCallback(singleSelectedCanvas?.boardId);
  const [showBatchExportImagesModal] = useAirModal(BatchExportImagesModal);
  const [showMoveCanvasToBoardModal] = useAirModal(MoveCanvasToBoardModal);
  const [showDeleteCanvasConfirmationModal] = useAirModal(DeleteCanvasConfirmationModal);

  const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
  const { showAddAssetsToBoardsModal } = useAddAssetsToBoardsModal();
  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  // Permissions
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const isRightPanelOpen = useWorkspaceRightPanelOpen();
  const { currentWorkspace } = useCurrentWorkspace();
  const { total: canvasCount } = useListCanvases({
    workspaceId: currentWorkspace?.id,
  });
  const { canConsumeMoreCredits } = useCanConsumeMoreCredits();

  return useMemo(() => {
    let options = [...getSelectionMenuOptions('action-bar', true)];
    let editTagsButton: ReactNode = null;
    let favoriteButton: ReactNode = null;
    let editTagsOption: DropdownMenuOption | null = null;
    let favoriteOption: DropdownMenuOption | null = null;
    let downloadAction: (() => void) | null = canDownloadAllClips && canDownloadAllBoards ? onBulkDownload : null;
    let downloadButton: ReactNode = downloadAction ? (
      <DownloadButton hasMultiple={true} onClick={downloadAction} />
    ) : null;
    let shareButton: ReactNode = null;
    let addToBoardButton: ReactNode = null;
    let customFieldsButton: ReactNode = null;
    let insertButton: ReactNode = null;
    let addToCanvasButton: ReactNode = null;
    let shareAction: (() => void) | null = null;

    const selectedCount = selectedItems.length;
    const selectedItem = selectedCount === 1 ? selectedItems[0] : null;
    const isSingleCanvasSelected = selectedItem?.type === GalleryItemType.canvas;
    const isSingleCanvasOnly = selectedCount === 1 && isSingleCanvasSelected;
    const hasCanvasSelected = selectedItems.some((item) => item.type === GalleryItemType.canvas);
    const isSplitView = generativeCanvasEnabled && isRightPanelOpen;
    const isInsertHidden = !isSplitView || isSingleCanvasOnly || !canConsumeMoreCredits;
    const showAddToCanvas =
      !isSplitView && generativeCanvasEnabled && selectedCount > 0 && !isSingleCanvasOnly && canConsumeMoreCredits;
    const shareButtonColor = showAddToCanvas ? 'grey' : undefined;

    // In non-split view, keep "add to board" as a dedicated button for multi-select.
    if (!isSplitView) {
      options = options.filter((option) => option.id !== 'add-to-board' && option.id !== 'add-to-board-separator');
    }

    // Handle single item selection
    if (selectedItems.length === 1) {
      const selectedItem = selectedItems[0];

      if (selectedItem.type === GalleryItemType.board) {
        const board = selectedBoardItems[0];
        const boardPermissions = getBoardPermissions(board.id);
        const canDownloadBoard = canGenerateZip(boardPermissions);

        options = getBoardMenuOptionsCallback({ item: board })({ trackLocation: 'action-bar' });
        // Boards don't support tag editing
        editTagsButton = null;
        favoriteButton = <FavoriteButton bookmarked={board.hasCurrentUser} onClick={() => onBoardFavorite(board)} />;
        favoriteOption = {
          ...getDropdownMenuOption(board.hasCurrentUser ? 'unfavorite' : 'favorite'),
          onSelect: () => onBoardFavorite(board),
          type: 'item',
        };
        shareAction = () => onBoardShare(board.id);
        shareButton = canViewShortUrl(boardPermissions) ? (
          <ShareButton color={shareButtonColor} onClick={shareAction} />
        ) : null;
        downloadAction = () => onBoardDownload(board.id);
        downloadButton = canDownloadBoard ? <DownloadButton hasMultiple={false} onClick={downloadAction} /> : null;
      } else if (selectedItem.type === GalleryItemType.asset) {
        const clip = selectedMediaClipItems[0];
        const clipPermissions = getClipPermissions(clip.id);
        const canEditTags = canEditClipTags(clipPermissions) && canRemoveClipTags(clipPermissions);
        const canShare = canCreateShortUrl(clipPermissions);
        const canDownloadClip = canDownloadVersion(clipPermissions);
        options = getAssetMenuOptionsCallback({ item: clip })({ trackLocation: 'action-bar' });
        editTagsButton = canEditTags ? (
          <EditTagsButton onClick={() => onSingleClipEditTags(clip.id, clip.tags || [])} />
        ) : null;
        editTagsOption = canEditTags
          ? {
              ...getDropdownMenuOption('edit-tags'),
              onSelect: () => onSingleClipEditTags(clip.id, clip.tags || []),
              type: 'item',
            }
          : null;
        favoriteButton = <FavoriteButton bookmarked={clip.bookmarked} onClick={() => onClipFavorite([clip])} />;
        favoriteOption = {
          ...getDropdownMenuOption(clip.bookmarked ? 'unfavorite' : 'favorite'),
          onSelect: () => onClipFavorite([clip]),
          type: 'item',
        };
        shareAction = () => onClipShare(clip, currentBoardId);
        shareButton = canShare ? <ShareButton color={shareButtonColor} onClick={shareAction} /> : null;
        downloadAction = () => onAssetDownload(clip.id);
        downloadButton = canDownloadClip ? <DownloadButton hasMultiple={false} onClick={downloadAction} /> : null;
      } else if (selectedItem.type === GalleryItemType.file) {
        const file = selectedFileItems[0];
        const clipPermissions = getClipPermissions(file.id);
        const canEditTags = canEditClipTags(clipPermissions) && canRemoveClipTags(clipPermissions);
        const canShare = canCreateShortUrl(clipPermissions);
        const canDownloadClip = canDownloadVersion(clipPermissions);
        options = getAssetMenuOptionsCallback({ item: file })({ trackLocation: 'action-bar' });
        editTagsButton = canEditTags ? (
          <EditTagsButton onClick={() => onSingleClipEditTags(file.id, file.tags || [])} />
        ) : null;
        editTagsOption = canEditTags
          ? {
              ...getDropdownMenuOption('edit-tags'),
              onSelect: () => onSingleClipEditTags(file.id, file.tags || []),
              type: 'item',
            }
          : null;
        favoriteButton = <FavoriteButton bookmarked={file.bookmarked} onClick={() => onClipFavorite([file])} />;
        favoriteOption = {
          ...getDropdownMenuOption(file.bookmarked ? 'unfavorite' : 'favorite'),
          onSelect: () => onClipFavorite([file]),
          type: 'item',
        };
        shareAction = () => onClipShare(file, currentBoardId);
        shareButton = canShare ? <ShareButton color={shareButtonColor} onClick={shareAction} /> : null;
        downloadAction = () => onAssetDownload(file.id);
        downloadButton = canDownloadClip ? <DownloadButton hasMultiple={false} onClick={downloadAction} /> : null;
      } else if (selectedItem.type === GalleryItemType.canvas) {
        if (singleSelectedCanvas) {
          options = filterCanvasTopBarMoreMenuOptions(
            getCanvasMenuOptionsCallback({ item: singleSelectedCanvas, trackLocation: 'action-bar' }),
          );
        }
        const boardId = currentBoardId;

        if (boardId && singleSelectedCanvas) {
          const boardPermissions = getBoardPermissions(boardId);
          shareAction = () => onCanvasShare(singleSelectedCanvas.id);
          shareButton = canViewShortUrl(boardPermissions) ? (
            <ShareButton color={shareButtonColor} onClick={shareAction} />
          ) : null;
        }
        editTagsButton = generativeCanvasEnabled ? null : editTagsButton;
        downloadButton = null;
        downloadAction = null;
      }
    }
    // Handle multiple clips selection
    else if (hasOnlyClipsSelected) {
      const areAllClipsFavorited = !selectedClipsHasUnfavorited(store.getState());
      // Combine both media clips and files for favoriting
      const allSelectedClips = [...selectedMediaClipItems, ...selectedFileItems];
      const canEditAllCustomFields = allSelectedClips.every(({ id }) =>
        canChangeAssetCustomFields(getClipPermissions(id)),
      );
      const canAddAllAssetsToABoard = allSelectedClips.every(({ id }) => canAddAssetToBoard(getClipPermissions(id)));
      favoriteButton = (
        <FavoriteButton bookmarked={areAllClipsFavorited} onClick={() => onClipFavorite(allSelectedClips)} />
      );
      editTagsButton = <EditTagsButton onClick={onMultipleClipsEditTags} />;
      editTagsOption = {
        ...getDropdownMenuOption('edit-tags'),
        onSelect: onMultipleClipsEditTags,
        type: 'item',
      };
      favoriteOption = {
        ...getDropdownMenuOption(areAllClipsFavorited ? 'unfavorite' : 'favorite'),
        onSelect: () => onClipFavorite(allSelectedClips),
        type: 'item',
      };
      customFieldsButton = canEditAllCustomFields ? (
        <CustomFieldsButton options={getCustomFieldsMenuOptions({ assets: allSelectedClips })} />
      ) : null;
      addToBoardButton = canAddAllAssetsToABoard ? (
        <AddToBoardButton
          onClick={() =>
            showAddAssetsToBoardsModal({
              clipIds: allSelectedClips.map((clip) => clip.id),
              trackLocation: 'action-bar',
              parentBoardId: currentBoardId,
            })
          }
        />
      ) : null;
      const hasExportableClip = allSelectedClips.some((clip) => isClipExportable(clip));
      if (hasExportableClip) {
        options.push({
          ...getDropdownMenuOption('export-as'),
          label: 'Export selection as',
          onSelect: () => showBatchExportImagesModal({ clips: allSelectedClips }),
          type: 'item',
        });
      }
    }
    // Bulk operations (tags, sharing, collections) are not supported for canvases; only move and delete apply.
    // Intersect per-canvas options so actions only appear when all selected canvases support them.
    else if (hasOnlyCanvasesSelected && selectedCount > 1) {
      downloadButton = null;
      downloadAction = null;
      shareButton = null;
      shareAction = null;

      const selectedCanvases = selectedItems
        .filter((item): item is SelectableGalleryCanvasItem => item.type === GalleryItemType.canvas)
        .map((item) => item.item);
      const selectedCanvasIds = selectedCanvases.map((canvas) => canvas.id);
      const targetCanvasOptionIds = new Set(['move-to', 'delete']);
      const allCanvasOptions = selectedCanvases.map((canvas) =>
        getCanvasMenuOptionsCallback({
          item: canvas,
          trackLocation: 'action-bar',
          boardId: canvas.boardId,
        }),
      );
      const canvasOptionIdsPerCanvas = allCanvasOptions.map((opts) =>
        opts.filter((option) => targetCanvasOptionIds.has(option.id)).map((option) => option.id),
      );
      const commonOptionIds = canvasOptionIdsPerCanvas.reduce<Set<string>>((acc, optionIds, index) => {
        if (index === 0) {
          return new Set(optionIds);
        }

        return new Set(optionIds.filter((id) => acc.has(id)));
      }, new Set<string>());

      const firstCanvasOptions = allCanvasOptions[0] ?? [];

      const commonBoardId =
        selectedCanvases.length > 0 &&
        selectedCanvases.every((canvas) => canvas.boardId === selectedCanvases[0].boardId)
          ? selectedCanvases[0].boardId
          : undefined;
      const moveTemplateOption = firstCanvasOptions.find(
        (option): option is Extract<DropdownMenuOption, { type: 'item' }> =>
          option.id === 'move-to' && option.type === 'item',
      );
      const deleteTemplateOption = firstCanvasOptions.find(
        (option): option is Extract<DropdownMenuOption, { type: 'item' }> =>
          option.id === 'delete' && option.type === 'item',
      );
      const bulkCanvasOptions: DropdownMenuOption[] = [];

      if (commonOptionIds.has('move-to') && moveTemplateOption) {
        bulkCanvasOptions.push({
          ...moveTemplateOption,
          onSelect: () =>
            showMoveCanvasToBoardModal({
              canvasIds: selectedCanvasIds,
              currentBoardId: commonBoardId,
              trackLocation: 'action-bar',
            }),
        });
      }

      if (commonOptionIds.has('delete') && deleteTemplateOption) {
        bulkCanvasOptions.push({
          ...deleteTemplateOption,
          onSelect: () =>
            showDeleteCanvasConfirmationModal({
              canvasIds: selectedCanvasIds,
              boardId: currentBoardId,
            }),
        });
      }

      options = bulkCanvasOptions;
    }

    if (selectedItems.length > 1 && collectionsEnabled && !hasOnlyCanvasesSelected) {
      shareAction = onCollectionShare;
      shareButton = <ShareCollectionButton color={shareButtonColor} onClick={shareAction} />;
    }

    if (!isInsertHidden && generativeCanvasEnabled && canvasCount > 0 && !hasOnlyCanvasesSelected && onInsert) {
      insertButton = <InsertButton count={insertCount} onClick={onInsert} />;
    }

    if (
      !isSplitView &&
      generativeCanvasEnabled &&
      selectedCount > 0 &&
      !hasCanvasSelected &&
      onAddToCanvas &&
      canConsumeMoreCredits
    ) {
      addToCanvasButton = (
        <AddToCanvasButton count={selectedCount > 1 ? selectedCount : undefined} onClick={onAddToCanvas} />
      );
    }

    if (isSplitView) {
      const downloadOption = downloadAction
        ? ({
            ...getDropdownMenuOption('download'),
            onSelect: downloadAction,
            type: 'item',
          } satisfies DropdownMenuOption)
        : null;
      const shareOption = shareAction
        ? ({ ...getDropdownMenuOption('share'), onSelect: shareAction, type: 'item' } satisfies DropdownMenuOption)
        : null;

      const optionsToInsert: DropdownMenuOption[] = [];

      if (editTagsOption) {
        optionsToInsert.push(editTagsOption);
        editTagsButton = null;
      }

      if (favoriteOption) {
        optionsToInsert.push(favoriteOption);
        favoriteButton = null;
      }

      const removeSeparatorIndex = options.findIndex((option) => option.id === 'delete-separator');
      const customFieldsOptionIds = new Set([
        'bulk-edit-custom-field',
        'bulk-edit-custom-fields',
        'edit-custom-fields',
      ]);
      const customFieldsIndex = options.findIndex((option) => customFieldsOptionIds.has(option.id));
      const baseInsertIndex = customFieldsIndex === -1 ? options.length : customFieldsIndex + 1;
      const insertIndex =
        removeSeparatorIndex === -1 ? baseInsertIndex : Math.min(baseInsertIndex, removeSeparatorIndex);
      const nextOptions = [...options];

      if (optionsToInsert.length > 0) {
        nextOptions.splice(insertIndex, 0, ...optionsToInsert);
      }

      if (downloadOption && !options.some((option) => option.id === downloadOption.id)) {
        const downloadInsertIndex = insertIndex + optionsToInsert.length;
        nextOptions.splice(downloadInsertIndex, 0, downloadOption);
      }

      if (shareOption && !options.some((option) => option.id === shareOption.id)) {
        const shareInsertIndex = downloadOption
          ? insertIndex + optionsToInsert.length + 1
          : insertIndex + optionsToInsert.length;
        nextOptions.splice(shareInsertIndex, 0, shareOption);
      }

      options = nextOptions;

      downloadButton = null;
      shareButton = null;
      addToBoardButton = null;
      customFieldsButton = null;
    }

    return {
      menuOptions: options,
      PrefixBarButtons: () => (
        <>
          {editTagsButton}
          {favoriteButton}
          {addToBoardButton}
          {customFieldsButton}
        </>
      ),
      SuffixBarButtons: () => (
        <>
          {insertButton}
          {downloadButton}
          {shareButton}
          {addToCanvasButton}
        </>
      ),
    };
  }, [
    getSelectionMenuOptions,
    canDownloadAllClips,
    canDownloadAllBoards,
    onBulkDownload,
    onInsert,
    generativeCanvasEnabled,
    isRightPanelOpen,
    onAddToCanvas,
    selectedItems,
    getCustomFieldsMenuOptions,
    selectedBoardItems,
    getBoardPermissions,
    getBoardMenuOptionsCallback,
    onBoardFavorite,
    onBoardShare,
    showAddAssetsToBoardsModal,
    showBatchExportImagesModal,
    currentBoardId,
    onBoardDownload,
    selectedMediaClipItems,
    getClipPermissions,
    getAssetMenuOptionsCallback,
    getCanvasMenuOptionsCallback,
    showDeleteCanvasConfirmationModal,
    showMoveCanvasToBoardModal,
    onCanvasShare,
    onSingleClipEditTags,
    onClipFavorite,
    onClipShare,
    onAssetDownload,
    selectedFileItems,
    singleSelectedCanvas,
    hasOnlyClipsSelected,
    hasOnlyCanvasesSelected,
    store,
    onMultipleClipsEditTags,
    onCollectionShare,
    collectionsEnabled,
    insertCount,
    canvasCount,
    canConsumeMoreCredits,
  ]);
};
